/**
 * VpnFormDialog (F24.3) — modal para crear o editar credencial OVPN.
 *
 * Modo crear: input descripción + combobox router opcional + botón generar.
 * Modo editar: descripción + router (puede desvincular) + activo toggle.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { useListRouters } from "@/features/routers/api";
import type { ApiError } from "@/lib/api";

import { useCrearCredencial, useEditarCredencial } from "./api";
import type { OvpnCredencial, OvpnCredencialesGeneradasResponse } from "./schemas";

type Props = {
  credencial: OvpnCredencial | null;  // null = modo crear
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (res: OvpnCredencialesGeneradasResponse) => void;
};

export function VpnFormDialog({ credencial, open, onOpenChange, onCreated }: Props) {
  const isEdit = credencial !== null;

  const [descripcion, setDescripcion] = useState("");
  const [routerId, setRouterId] = useState<string>("");
  const [activo, setActivo] = useState(true);

  const { data: routersData } = useListRouters({ size: 200 });
  const routerOptions: ComboboxOption[] = (routersData?.items ?? []).map((r) => ({
    value: r.id,
    label: r.nombre,
    hint: r.ip_address,
  }));

  const crearMut = useCrearCredencial();
  const editarMut = useEditarCredencial(credencial?.id ?? "");

  useEffect(() => {
    if (open) {
      if (credencial) {
        setDescripcion(credencial.descripcion);
        setRouterId(credencial.router_id ?? "");
        setActivo(credencial.activo);
      } else {
        setDescripcion("");
        setRouterId("");
        setActivo(true);
      }
    }
  }, [open, credencial]);

  async function handleSubmit() {
    if (!descripcion.trim()) {
      toast.error("Descripción requerida",
        { description: "Ej. \"Router Telmex Centro\", \"Laptop soporte Juan\"." });
      return;
    }

    if (isEdit) {
      try {
        // En edit, mandamos router_id explícito (puede ser null para desvincular).
        // Como el backend usa model_fields_set para detectar "set None", aquí
        // siempre se manda el campo.
        await editarMut.mutateAsync({
          descripcion: descripcion.trim(),
          router_id: routerId || null,
          activo,
        });
        toast.success("Credencial actualizada");
        onOpenChange(false);
      } catch (e) {
        toast.error("No se pudo actualizar", { description: (e as ApiError).message });
      }
      return;
    }

    // Crear
    try {
      const res = await crearMut.mutateAsync({
        descripcion: descripcion.trim(),
        router_id: routerId || null,
      });
      toast.success("Credencial creada");
      onCreated(res);
    } catch (e) {
      toast.error("No se pudo crear", { description: (e as ApiError).message });
    }
  }

  const isPending = crearMut.isPending || editarMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={16} />
            {isEdit ? "Editar credencial VPN" : "Nueva credencial VPN"}
          </DialogTitle>
          {!isEdit && (
            <p className="text-xs text-muted-foreground">
              Genera un nuevo usuario y password OVPN. La password se mostrará{" "}
              <strong>una sola vez</strong> después de crear — guárdala.
            </p>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Descripción *</Label>
            <Input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej. Router Telmex Centro, Laptop soporte Juan"
              autoFocus
            />
            <p className="text-[10px] text-muted-foreground">
              Para identificarla después en el listado.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-xs">Vincular a router (opcional)</Label>
            <Combobox
              options={routerOptions}
              value={routerId}
              onChange={setRouterId}
              placeholder="— Sin asignar —"
              allowClear
            />
            <p className="text-[10px] text-muted-foreground">
              Si esta credencial es para un Mikrotik administrado, vincúlalo.
              Si es para un técnico/laptop, déjalo en blanco.
            </p>
          </div>

          {isEdit && (
            <label className="flex items-start gap-2 cursor-pointer text-xs pt-1">
              <input
                type="checkbox"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <span className="font-medium">Credencial activa</span>
                <div className="text-[10px] text-muted-foreground">
                  Si la desactivas, el usuario no puede autenticarse pero la
                  credencial se mantiene (puedes reactivar después).
                </div>
              </div>
            </label>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={isPending}>
              {isPending
                ? (isEdit ? "Guardando…" : "Generando…")
                : (isEdit ? "Guardar" : "Generar credencial")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
